import Immutable from 'immutable';
import type { AspectRatio } from '@lumen5/luminary';

import {
    type DashboardVideo,
    type EstimatedStatusEnum,
    type NarrativeThreadEnum,
    RoleEnum,
    type UserProfile,
    type VideoSortBy,
    type VideoStatus,
} from 'js/src/client';
import {
    getActiveWorkspaceMembers,
    selectActiveWorkspace,
    selectUsersWithAccessToWorkspaceAndProject,
} from 'js/src/data/account';
import type { RenderEstimateStatus, View } from 'js/src/data/dashboard/dashboard-types';
import {
    NON_PROJECT_VIEW_IDS,
    VIDEOS_KEY,
    viewIdIsProject,
    viewIdIsProjectOrAllVideos,
} from 'js/src/data/dashboard/dashboard-types';
import StoreORM from 'js/src/data/store-orm';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

/* SELECTORS  */
export function selectActiveView(state: RootState) {
    return state.dashboard.activeView;
}

export function selectActiveViewIsProject(state: RootState) {
    const activeView = selectActiveView(state);
    return viewIdIsProject(activeView);
}

export function selectActiveViewIsProjecOrAllVideos(state: RootState) {
    const activeView = selectActiveView(state);
    return viewIdIsProjectOrAllVideos(activeView);
}

export function selectViewData(state: RootState, viewId: number) {
    const viewOrm = new StoreORM<View>(state.dashboard.views);
    return viewOrm.getByIdOrUndefined(viewId);
}

export function viewIsLoaded(state: RootState, viewId: number) {
    return !!selectViewData(state, viewId);
}

export function selectViewIds(state: RootState) {
    const viewOrm = new StoreORM<View>(state.dashboard.views);
    return viewOrm.getAllIds();
}

export function selectActiveViewData(state: RootState) {
    const activeView = selectActiveView(state);
    const view = selectViewData(state, activeView);
    if (!view) throw new Error(`View with id ${activeView} not found`);
    return view;
}

export function selectViewName(state: RootState, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    return viewData.get('name');
}

export function selectActiveViewSubTitle(state: RootState) {
    const viewData = selectActiveViewData(state);
    return viewData.get('subTitle');
}

export function selectActiveViewName(state: RootState) {
    const activeView = selectActiveView(state);
    return selectViewName(state, activeView);
}

export function selectViewIcon(state: RootState, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    return viewData.get('icon');
}

export function selectActiveViewIcon(state: RootState) {
    const activeView = selectActiveView(state);
    return selectViewIcon(state, activeView);
}

export function selectViewColor(state: RootState, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    return viewData.get('color');
}

export function selectActiveViewColor(state: RootState) {
    const activeView = selectActiveView(state);
    return selectViewColor(state, activeView);
}

const EMPTY_LIST = Immutable.List<ImmutableMap<UserProfile>>();
export function selectActiveViewMembers(
    state: RootState,
): Immutable.List<ImmutableMap<UserProfile>> {
    const activeView = selectActiveView(state);
    if (NON_PROJECT_VIEW_IDS.includes(activeView)) {
        return getActiveWorkspaceMembers(state);
    } else {
        // activeView is a project!
        const activeWorkspace = selectActiveWorkspace(state);
        if (!activeWorkspace) {
            return EMPTY_LIST;
        }
        return selectUsersWithAccessToWorkspaceAndProject(
            state,
            activeWorkspace.get('id'),
            activeView,
        );
    }
}

export function selectAllVideoIds(state: RootState): Immutable.List<number> {
    const viewData = selectActiveViewData(state);
    const videosOrm = new StoreORM<DashboardVideo>(viewData.get(VIDEOS_KEY));
    return videosOrm.getAllIds();
}

export function selectVideoDurationById(state: RootState, videoId: number): number | null {
    const video = selectVideoById(state, videoId);
    return video?.get('duration_seconds') || null;
}

export function selectVideoById(state: RootState, videoId: number) {
    const viewData = selectActiveViewData(state);
    const videosOrm = new StoreORM<DashboardVideo>(viewData.get(VIDEOS_KEY));
    return videosOrm.getByIdOrUndefined(videoId);
}

export function selectVideoByIdInView(state: RootState, videoId: number, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    const videosOrm = new StoreORM<DashboardVideo, number>(viewData.get(VIDEOS_KEY));
    return videosOrm.getByIdOrUndefined(videoId);
}

export function selectNumVideos(state: RootState): number {
    const viewData = selectActiveViewData(state);
    const videosOrm = new StoreORM<DashboardVideo, number>(viewData.get(VIDEOS_KEY));
    return videosOrm.length;
}

export function selectLoadedUpToPage(state: RootState): number {
    const viewData = selectActiveViewData(state);
    return viewData.get('loadedUpToPage');
}

export function selectFilterVideosByUserId(state: RootState): number | undefined {
    const viewData = selectActiveViewData(state);
    return viewData.get('filterByUserId');
}

export function getVideoSharePageUrl(state: RootState, videoOrmId: number): string {
    const video = selectVideoById(state, videoOrmId);
    if (!video) throw new Error(`Video with id ${videoOrmId} not found`);
    return video.get('page_url');
}

export function getMoreVideosToLoad(state: RootState): boolean {
    const viewData = selectActiveViewData(state);
    return viewData.get('moreVideosToLoad');
}

export function getVideosSortBy(state: RootState): VideoSortBy {
    const viewData = selectActiveViewData(state);
    return viewData.get('videosSortBy');
}

export function selectFilterVideosByNarrativeThread(
    state: RootState,
): NarrativeThreadEnum | undefined {
    const viewData = selectActiveViewData(state);
    return viewData.get('filterByNarrativeThread');
}

export function selectFilterVideosByAspectRatio(state: RootState): AspectRatio | undefined {
    const viewData = selectActiveViewData(state);
    return viewData.get('filterByAspectRatio');
}

export function selectFilterVideosByStatus(state: RootState): VideoStatus | undefined {
    const viewData = selectActiveViewData(state);
    return viewData.get('filterByStatus');
}

export function selectFilterVideosByTemplateId(state: RootState): string | undefined {
    const viewData = selectActiveViewData(state);
    return viewData.get('filterByTemplateId');
}

export function noProjects(state: RootState): boolean {
    const viewIds = selectViewIds(state);

    // Return true if there are no projects in the active workspace
    return (
        viewIds.filter((viewId) => {
            return (
                viewIdIsProject(viewId) &&
                selectViewData(state, viewId)?.get('workspaceId') ===
                    selectActiveWorkspace(state)?.get('id')
            );
        }).size <= 0
    );
}

export function isViewEmpty(state: RootState, viewId: number): boolean {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    const videosOrm = new StoreORM<DashboardVideo>(viewData.get(VIDEOS_KEY));
    return videosOrm.isEmpty();
}

export function getVideoSearchTerm(state: RootState): string {
    if (hasDashboardState(state)) {
        return state.dashboard.videoSearchTerm;
    } else {
        return '';
    }
}

export function selectRenderEstimatedStatus(
    state: RootState,
    videoId: number,
): EstimatedStatusEnum | undefined {
    const orm = new StoreORM<RenderEstimateStatus>(state.dashboard.estimatedRenderDurations);
    return orm.getByIdOrUndefined(videoId)?.get('estimated_status');
}

export function selectRenderEstimatedTimeRemaining(
    state: RootState,
    videoId: number,
): number | undefined {
    const orm = new StoreORM<RenderEstimateStatus>(state.dashboard.estimatedRenderDurations);
    return orm.getByIdOrUndefined(videoId)?.get('estimated_time_remaining');
}

export function hasDashboardState(state: RootState): boolean {
    return Object.prototype.hasOwnProperty.call(state, 'dashboard');
}

export function canCreateVideos(state: RootState) {
    if (SERVER.USER.role === RoleEnum.ADMIN) {
        return true;
    }
    if (SERVER.USER.role === RoleEnum.EDITOR && state.dashboard) {
        // Editors can only create videos if they are in the project
        const activeViewIsProject = selectActiveViewIsProject(state);
        return activeViewIsProject;
    }
    // All other users cannot create videos
    return false;
}
